import React from 'react'
import Img from 'gatsby-image'

// Diagnostic : explique POURQUOI une image ne peut pas être rendue.
// Distingue les 3 ruptures possibles de la chaîne image -> localFile -> childImageSharp.
// Le message est visible dans le terminal du build/develop (y compris en devcontainer)
// et dans la console du navigateur.
const diagnoseImage = (attrs, images) => {
	const imageid = attrs && attrs.imageid
	if (!imageid) {
		return `attrs.imageid absent ou nul (valeur=${JSON.stringify(imageid)}, type ${typeof imageid})`
	}
	if (!images || !images.length) {
		return `le contexte de page ne contient aucune image (tableau "images" vide/absent dans pageContext)`
	}
	const image = images.find(({wordpress_id}) => wordpress_id === imageid)
	if (!image) {
		const ids = images.map(i => i && i.wordpress_id)
		return `aucune image avec wordpress_id === ${JSON.stringify(imageid)} (type ${typeof imageid}). `
			+ `IDs présents dans le contexte : [${ids.join(', ')}]. `
			+ `Causes probables : (a) format filtré dans gatsby-node.js (regex /\\.(jpg|jpeg|png|gif|webp)$/ `
			+ `=> SVG/avif/bmp/tiff exclus du build) ; (b) incohérence de type imageid (string) vs wordpress_id (number).`
	}
	if (!image.localFile) {
		return `image trouvée (wordpress_id=${image.wordpress_id}) mais localFile=null : `
			+ `gatsby-source-wordpress n'a pas téléchargé le fichier. `
			+ `source_url=${image.source_url}. `
			+ `En devcontainer, vérifier que cette URL est joignable depuis le conteneur de build.`
	}
	if (!image.localFile.childImageSharp) {
		return `image trouvée et localFile présent (wordpress_id=${image.wordpress_id}) mais childImageSharp=null : `
			+ `sharp n'a pas pu traiter le fichier (corrompu, vide, ou format non raster). `
			+ `source_url=${image.source_url}.`
	}
	return null
}

const Image = ({attrs, images}) => {
	const image = attrs && attrs.imageid && images && images.find(({wordpress_id}) => wordpress_id === attrs.imageid)
	const imageAlt = attrs && attrs.image_alt
	const imageAhref = attrs && attrs.image_ahref
	const imageAtarget = attrs && attrs.image_atarget
	const imageCaption = attrs && attrs.image_caption

	// Fallback pour l'alt text
	const altText = imageAlt || (image && image.title) || 'Image sans description'

	// Gestion des liens externes
	const isExternalLink = imageAhref && (imageAhref.startsWith('http://') || imageAhref.startsWith('https://'))
	const linkRel = isExternalLink ? 'noopener noreferrer' : undefined

	const canRender = image && image.localFile && image.localFile.childImageSharp
	let diagnostic = null
	if (!canRender) {
		diagnostic = diagnoseImage(attrs, images)
		// eslint-disable-next-line no-console
		console.warn(`[Core/Image] "Image corrompue ou manquante" — ${diagnostic}`)
	}

	const imageContent = canRender ? (
		<Img
			alt={altText}
			sizes={image.localFile.childImageSharp.sizes}
			role="img"
			aria-hidden="false"
		/>
	) : (
		<p role="alert" aria-live="polite">
			Image corrompue ou manquante
			{process.env.NODE_ENV === 'development' && diagnostic ? ` — ${diagnostic}` : null}
		</p>
	)

	const captionContent = imageCaption ? (
		<p className='image-caption' aria-label="Légende de l'image">
			<span dangerouslySetInnerHTML={{__html: imageCaption}} />
		</p>
	) : null

	if ( imageAhref ) {
		return (
			<a
				href={imageAhref}
				target={imageAtarget}
				{...(linkRel && { rel: linkRel })}
				aria-label={`${altText}${imageCaption ? ` - ${imageCaption.replace(/<[^>]*>/g, '')}` : ''}`}
			>
				{imageContent}
				{captionContent}
			</a>
		)
	} else {
		return (
			<>
				{imageContent}
				{captionContent}
			</>
		)
	}
}

export default Image
